import { Inject, Injectable } from '@angular/core';

import { Observable, Subject } from 'rxjs';

import { AppTheme } from './theme-switch.interface';
import { IconService } from '@tablesready/frontend-icons';
import { STORAGE_TOKEN } from '@tablesready/frontend-storage';

const STORAGE_KEY = 'app-theme';

@Injectable({ providedIn: 'root' })
export class ThemeSwitchService {
  themeChange$: Observable<AppTheme>;

  private readonly themeChangeSource$ = new Subject<AppTheme>();

  constructor(
    private readonly iconService: IconService,
    @Inject(STORAGE_TOKEN) private readonly storage: Storage,
  ) {
    this.themeChange$ = this.themeChangeSource$.asObservable();
  }

  private static getRoot(): HTMLElement | null {
    return document.getElementsByTagName('html').item(0);
  }

  setTheme(theme: AppTheme): void {
    if (this.themeChangeSource$) {
      this.themeChangeSource$.next(theme);
    }

    const rootElement = ThemeSwitchService.getRoot();
    if (!rootElement) {
      throw new Error('no root element found to detemine theme');
    }

    this.storage.setItem(STORAGE_KEY, theme);

    rootElement.classList.forEach((className) => {
      if (className?.includes('-theme')) {
        rootElement.classList.remove(className);
      }
    });

    const metaTags = document.getElementsByTagName('meta');
    for (let i = 0; i < metaTags.length; i++) {
      const tagEl = metaTags.item(i);
      if (!tagEl) {
        continue;
      }

      if (tagEl.name === 'color-scheme') {
        tagEl.setAttribute('content', theme === AppTheme.dark ? 'dark' : 'light');
      }
    }

    rootElement.classList.add(theme);
    document.body.classList.toggle('dark', theme === AppTheme.dark);

    if (theme === AppTheme.dark) {
      rootElement.classList.add('dark');
      this.iconService.setIconsLight();
    } else {
      this.iconService.setIconsDark();
    }
  }

  initializeTheme(): void {
    this.setTheme(this.getSavedTheme());
  }

  getSavedTheme(): AppTheme {
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)');
    let theme = this.storage.getItem(STORAGE_KEY) as AppTheme;

    if (!theme) {
      theme = prefersDark.matches ? AppTheme.dark : AppTheme.light;
      this.setTheme(theme);
      return theme;
    }

    return theme;
  }
}
